// @givmo/design-system — Meter (SaaS layer).
//
// A labeled gauge for "how much of a whole" — program utilization, enrollment rate, a fundraiser goal.
// Two shapes off one value/max contract: `linear` (a bar, the dashboard/table default) and `radial`
// (a ring, for a compact tile). Tones carry MEANING, never decoration: `brand` is neutral progress,
// while `warning`/`danger` are for a caller that has decided a level is worth flagging — the meter
// never picks a tone from the value itself, because "high" is good for enrollment and bad for budget.
//
// THE METER NEVER FORMATS A NUMBER. `valueLabel` is whatever the caller already formatted (money is a
// decimal string in this repo and is only ever formatted, never parsed), and `valueText` is the string
// screen readers hear. The meter only computes the *geometry* percentage.
//
// a11y: `role="progressbar"` with `aria-valuenow/min/max` + `aria-valuetext`. `progressbar` is chosen
// deliberately over ARIA 1.2's semantically-tidier `role="meter"`: `progressbar` is announced reliably
// by every current screen reader, and an unannounced gauge is worse than an imprecise role. The bar is
// non-interactive, so there is nothing to put in the tab order.
//
// The accessible NAME is wired explicitly, both shapes: a visible `label` gets an `id` and the gauge
// points at it with `aria-labelledby`; with no visible label the gauge falls back to `aria-label` from
// the `ariaLabel` prop. Rendering the label as a sibling `<span>` is NOT enough on its own — a sibling
// never enters accessible-name computation, so the earlier version of this file left
// `<Meter label="…"/>` as a NAMELESS progressbar while claiming otherwise.
import { useId } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

export type MeterTone = 'brand' | 'success' | 'warning' | 'danger';
export type MeterShape = 'linear' | 'radial';
export type MeterSize = 'sm' | 'md';

export interface MeterProps extends TestIdProps {
  /** Current value. Clamped into `[0, max]` for the geometry; the label is the caller's own text. */
  value: number;
  /** Upper bound (default 100). A `max <= 0` renders an empty meter rather than dividing by zero. */
  max?: number;
  shape?: MeterShape;
  tone?: MeterTone;
  size?: MeterSize;
  /** Visible label — above the bar (linear) or beneath the ring (radial). Also the accessible name. */
  label?: ReactNode;
  /** Caller-formatted value text, shown opposite the label (linear) or inside the ring (radial). */
  valueLabel?: ReactNode;
  /** What a screen reader announces for the value. Defaults to the rounded percentage. */
  valueText?: string;
  /** Accessible name when there is no visible `label`. */
  ariaLabel?: string;
  className?: string;
}

const FILL_COLOR: Record<MeterTone, string> = {
  brand: 'bg-brand-500',
  success: 'bg-success-500',
  warning: 'bg-warning-500',
  danger: 'bg-danger-500',
};

const STROKE_COLOR: Record<MeterTone, string> = {
  brand: 'text-brand-500',
  success: 'text-success-500',
  warning: 'text-warning-500',
  danger: 'text-danger-500',
};

const TRACK_HEIGHT: Record<MeterSize, string> = {
  sm: 'h-6',
  md: 'h-8',
};

// `size-48` resolves through the theme token (`--spacing-48` exists). There is NO `--spacing-72` —
// the scale jumps 48 → 84 — and Tailwind v4 does not error on a missing key: it silently falls back to
// its own 4px base, so the bare numeric form would emit `calc(var(--spacing) * 72)` = 288px instead of
// 72px. Per the build_and_ci.md px-scale rule (confirm the key, or write the arbitrary value), `md` is
// explicit. Do NOT spell the bare utility out in prose here: Tailwind scans source as plain TEXT, so a
// class name written in a comment still emits its rule — mentioning the broken one re-creates it.
const RING_SIZE: Record<MeterSize, string> = {
  sm: 'size-48',
  md: 'size-[72px]',
};

const RING_VALUE_TEXT: Record<MeterSize, string> = {
  sm: 'text-s11',
  md: 'text-s14',
};

/** Geometry only: the clamped 0–100 percentage. Never used for display without the caller's label. */
function fillPercent(value: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
  if (value <= 0) return 0;
  return Math.min(100, (value / max) * 100);
}

export function Meter({
  value,
  max = 100,
  shape = 'linear',
  tone = 'brand',
  size = 'md',
  label,
  valueLabel,
  valueText,
  ariaLabel,
  className,
  testId,
}: MeterProps) {
  const percent = fillPercent(value, max);
  const announced = valueText ?? `${Math.round(percent)}%`;

  // One id, referenced by whichever shape renders the visible label below.
  const labelId = useId();
  const hasVisibleLabel = label != null;

  // The a11y contract is identical across shapes — only the geometry differs.
  const gaugeAria = {
    role: 'progressbar' as const,
    'aria-valuenow': Math.min(Math.max(value, 0), Math.max(max, 0)),
    'aria-valuemin': 0,
    'aria-valuemax': Math.max(max, 0),
    'aria-valuetext': announced,
    // Exactly one naming mechanism is active: point at the visible label, or carry `ariaLabel`.
    'aria-labelledby': hasVisibleLabel ? labelId : undefined,
    'aria-label': hasVisibleLabel ? undefined : ariaLabel,
  };

  if (shape === 'radial') {
    return (
      <div
        className={cn('inline-flex flex-col items-center gap-8', className)}
        {...testIdProp(testId)}
      >
        <div className={cn('relative', RING_SIZE[size])}>
          <svg viewBox="0 0 36 36" className="size-full -rotate-90" {...gaugeAria}>
            <circle
              cx="18"
              cy="18"
              r="16"
              fill="none"
              strokeWidth="3.5"
              className="stroke-neutral-200"
            />
            {percent > 0 && (
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                strokeWidth="3.5"
                strokeLinecap="round"
                // `pathLength=100` makes the dash units literal percentages, so no circumference math.
                pathLength={100}
                strokeDasharray={`${percent} 100`}
                className={cn('stroke-current', STROKE_COLOR[tone])}
              />
            )}
          </svg>
          {valueLabel != null && (
            <span
              className={cn(
                'absolute inset-0 flex items-center justify-center font-sans font-w600',
                'tracking-normal text-text-strong tabular-nums',
                RING_VALUE_TEXT[size],
              )}
            >
              {valueLabel}
            </span>
          )}
        </div>
        {label != null && (
          <span
            id={labelId}
            className="font-sans text-s12 font-w500 tracking-normal text-text-tertiary"
          >
            {label}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-6', className)} {...testIdProp(testId)}>
      {(label != null || valueLabel != null) && (
        <div className="flex items-baseline justify-between gap-12">
          {label != null && (
            <span
              id={labelId}
              className="font-sans text-s13 font-w500 tracking-normal text-text-secondary"
            >
              {label}
            </span>
          )}
          {valueLabel != null && (
            <span className="font-sans text-s13 font-w600 tracking-normal text-text-strong tabular-nums">
              {valueLabel}
            </span>
          )}
        </div>
      )}
      <div
        className={cn('w-full overflow-hidden rounded-full bg-neutral-200', TRACK_HEIGHT[size])}
        {...gaugeAria}
      >
        <div
          className={cn('h-full rounded-full transition-[width]', FILL_COLOR[tone])}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
